import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { ArrowUpRight } from "lucide-react";
import { insights } from "../data/insights";
import { useLang } from "../i18n/LanguageProvider";

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
}

export default function Insights() {
  const { t } = useLang();
  const [featured, ...rest] = insights;

  return (
    <>
      <PageHeader
        eyebrow={t("page.insights.eyebrow")}
        title={t("page.insights.title")}
        lede={t("page.insights.lede")}
      />

      {/* FEATURED */}
      <section className="pb-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Link
            to={`/insights/${featured.slug}`}
            className="group block border border-[var(--bw-border)] p-8 lg:p-14 relative overflow-hidden hover:border-[var(--bw-gold)]/60 transition-colors"
            data-testid={`insight-featured`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-8">
                <div className="text-gold text-[10px] tracking-[0.32em] uppercase mb-4">
                  {featured.category} · {featured.reading}
                </div>
                <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05] group-hover:text-gold transition-colors">
                  {featured.title}
                </h2>
                <p className="text-muted mt-6 leading-relaxed max-w-2xl text-lg">
                  {featured.dek}
                </p>
                <div className="mt-8 flex items-center gap-6 text-sm text-muted">
                  <span>{featured.author}</span>
                  <span className="w-6 h-px bg-[var(--bw-border)]" />
                  <span>{formatDate(featured.date)}</span>
                </div>
              </div>
              <div className="lg:col-span-4 lg:justify-self-end flex items-start">
                <div className="inline-flex items-center gap-3 text-gold text-sm tracking-[0.22em] uppercase group-hover:gap-5 transition-all">
                  {t("insights.readMore")} <ArrowUpRight size={16} />
                </div>
              </div>
            </div>
            <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[var(--bw-gold)] opacity-[0.05] blur-3xl" />
          </Link>
        </div>
      </section>

      {/* LIST */}
      <section className="pb-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-0 border-t border-[var(--bw-border)]">
            {rest.map((it, i) => (
              <Link
                key={it.slug}
                to={`/insights/${it.slug}`}
                className={`group py-10 lg:py-12 border-b border-[var(--bw-border)] ${
                  i % 2 === 0 ? "md:border-r md:pr-10" : "md:pl-10"
                }`}
                data-testid={`insight-${it.slug}`}
              >
                <div className="text-gold text-[10px] tracking-[0.32em] uppercase mb-3">
                  {it.category} · {it.reading}
                </div>
                <h3 className="font-serif-display text-2xl lg:text-3xl text-white group-hover:text-gold transition-colors leading-tight">
                  {it.title}
                </h3>
                <p className="text-muted mt-4 leading-relaxed">{it.dek}</p>
                <div className="mt-6 flex items-center justify-between text-sm">
                  <div className="text-muted">
                    {it.author} · {formatDate(it.date)}
                  </div>
                  <ArrowUpRight
                    size={16}
                    className="text-gold opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
